import { getValidationApiProblem } from '@mkp/shared/data-access';

export const PUBLICATION_DAYS_PROPERTY_PATH = 'product.publicationDays';

const ALLOWED_VALUES_PARAMETER = '{{ allowedValuesForProductType }}';

export type PublicationDaysViolation =
  | { kind: 'absent' }
  | { kind: 'usable'; allowedDays: number }
  | { kind: 'unusable'; allowedValues: string[] };

const parseAllowedValues = (raw: unknown): string[] =>
  (Array.isArray(raw)
    ? raw
    : String(raw)
        .replace(/^[\s[]+/, '')
        .replace(/[\s\]]+$/, '')
        .split(',')
  )
    .map((value) => String(value).trim())
    .filter(Boolean);

const isRetryableDayCount = (allowedValues: string[], allowedDays: number): boolean =>
  allowedValues.length === 1 && Number.isInteger(allowedDays) && allowedDays > 0;

const RETRYABLE_STATUS = 400;

/**
 * A 5xx can carry a violations body while the write has already been committed, so
 * replaying it would create a second credit. Only a status core-api uses to reject
 * the payload is safe to retry; an unwrapped body carries no status to judge.
 */
const hasNonRetryableStatus = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null | undefined)?.status;

  return status !== undefined && status !== RETRYABLE_STATUS;
};

export const getPublicationDaysViolation = (error: unknown): PublicationDaysViolation => {
  if (hasNonRetryableStatus(error)) {
    return { kind: 'absent' };
  }

  const problem = getValidationApiProblem(error);
  const violation = problem?.violations.find(
    (candidate) => candidate?.propertyPath === PUBLICATION_DAYS_PROPERTY_PATH
  );
  const rawAllowedValues = violation?.parameters?.[ALLOWED_VALUES_PARAMETER];

  if (rawAllowedValues == null) {
    return { kind: 'absent' };
  }

  const allowedValues = parseAllowedValues(rawAllowedValues);
  const allowedDays = Number(allowedValues[0]);

  return isRetryableDayCount(allowedValues, allowedDays)
    ? { kind: 'usable', allowedDays }
    : { kind: 'unusable', allowedValues };
};
